import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Api, Profile } from "../types";
import { localizeError } from "../auth/localizeError";
import { ThemeProvider } from "../theme/ThemeProvider";
import { readDisplayPreferences, storeDisplayPreferences } from "../theme/bootstrap";
import { normalizeLocale, type AppLocale, type PreferencePatch, type ThemePreference } from "./types";

type Preferences = {
  locale: AppLocale;
  theme: ThemePreference;
  profile?: Profile;
  save(patch: PreferencePatch): Promise<Profile | undefined>;
  submitOnboarding(action: "draft" | "complete" | "skip", body: Record<string, unknown>): Promise<Profile>;
  replaceProfile(profile: Profile): void;
  busy: boolean;
  error: string;
};
const Context = createContext<Preferences | null>(null);
const merge = (profile: Profile, patch: PreferencePatch): Profile => ({
  ...profile, ...patch, preferences: { ...profile.preferences, ...patch.preferences },
});
const cancelled = () => Object.assign(new Error("Preference update cancelled"), { code: "preferences_conflict" });

export function PreferencesProvider({ api, initialProfile, children, optimistic = true }: {
  api?: Api; initialProfile?: Profile; children: ReactNode; optimistic?: boolean;
}) {
  const [display, setDisplay] = useState(readDisplayPreferences);
  const [profile, setProfile] = useState<Profile | undefined>(initialProfile);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const confirmed = useRef(initialProfile);
  const alive = useRef(true);
  const epoch = useRef(0);
  const queue = useRef(Promise.resolve());
  const pending = useRef<{ token: symbol; patch?: PreferencePatch }[]>([]);
  const channel = useRef<BroadcastChannel | null>(null);
  const refreshAfterSave = useRef(false);
  const locale = profile ? normalizeLocale(profile.locale) : display.locale;
  const theme = profile ? (profile.preferences?.theme === "dark" ? "dark" : "light") : display.theme;

  function publish() {
    if (!alive.current) return;
    const base = confirmed.current;
    // Mobile language changes affect prompts and memory, so callers may wait for the server.
    setProfile(base && optimistic ? pending.current.reduce((next, item) => item.patch ? merge(next, item.patch) : next, base) : base);
    setBusy(pending.current.length > 0);
  }
  function replaceProfile(next: Profile) {
    if (!alive.current || next.id !== confirmed.current?.id) return;
    if ((next.preferences_revision || 0) < (confirmed.current.preferences_revision || 0)) return;
    confirmed.current = next;
    publish();
  }
  async function reload() {
    if (!api || !confirmed.current) return;
    const next = await api.request<Profile>("/me");
    replaceProfile(next);
  }
  useEffect(() => {
    alive.current = true;
    if (api && typeof BroadcastChannel !== "undefined") {
      const bus = new BroadcastChannel("lyncia.preferences");
      channel.current = bus;
      bus.onmessage = event => {
        if (event.data?.type !== "changed" || !Number.isInteger(event.data.revision)) return;
        if (pending.current.length) refreshAfterSave.current = true;
        else void reload().catch(error => { if (alive.current) setFailure(error); });
      };
    }
    return () => {
      alive.current = false;
      epoch.current += 1;
      channel.current?.close();
    };
  }, [api]);
  useEffect(() => {
    // Only display preferences are cached. Never persist identity or account data here.
    if (!pending.current.length) storeDisplayPreferences(locale, theme);
  }, [locale, theme, busy]);

  function enqueue(path: string, method: string, body: Record<string, unknown>, patch?: PreferencePatch): Promise<Profile> {
    if (!api || !confirmed.current) return Promise.reject(new Error("Authentication required"));
    const apiClient = api;
    const token = Symbol();
    const generation = epoch.current;
    pending.current.push({ token, patch });
    setFailure(null);
    publish();
    const task = queue.current.then(async () => {
      if (!alive.current || generation !== epoch.current) throw cancelled();
      const previous = confirmed.current!;
      try {
        const next = await apiClient.request<Profile>(path, {
          method, body: { ...body, expected_revision: previous.preferences_revision || 0 },
        });
        if (!alive.current || generation !== epoch.current) throw cancelled();
        if (next.id !== previous.id || !Number.isInteger(next.preferences_revision) ||
          next.preferences_revision! < (previous.preferences_revision || 0) ||
          (patch && next.preferences_revision! <= (previous.preferences_revision || 0))) {
          throw Object.assign(new Error("The server does not support this preferences version"), { code: "preferences_unavailable" });
        }
        confirmed.current = next;
        channel.current?.postMessage({ type: "changed", revision: next.preferences_revision });
        return next;
      } catch (error) {
        if (alive.current && generation === epoch.current) {
          // Never replay old choices after a conflict. Refresh and require an explicit retry.
          epoch.current += 1;
          pending.current = [];
          setFailure(error);
          if ((error as { status?: number; code?: string }).status === 409 ||
            (error as { code?: string }).code === "preferences_conflict") {
            try { await reload(); } catch { /* Preserve the last confirmed state and the original error. */ }
          }
        }
        throw error;
      } finally {
        pending.current = pending.current.filter(item => item.token !== token);
        publish();
        if (alive.current && !pending.current.length && refreshAfterSave.current) {
          refreshAfterSave.current = false;
          void reload().catch(error => { if (alive.current) setFailure(error); });
        }
      }
    });
    queue.current = task.then(() => undefined, () => undefined);
    return task;
  }
  async function save(patch: PreferencePatch) {
    if (!confirmed.current) {
      setFailure(null);
      setDisplay(old => ({ locale: patch.locale || old.locale, theme: patch.preferences?.theme || old.theme }));
      return undefined;
    }
    return enqueue("/me", "PATCH", patch, patch);
  }
  function submitOnboarding(action: "draft" | "complete" | "skip", body: Record<string, unknown>) {
    return enqueue(action === "draft" ? "/me/onboarding" : `/me/onboarding/${action}`, action === "draft" ? "PUT" : "POST", body);
  }
  return <Context.Provider value={{ locale, theme, profile, save, submitOnboarding, replaceProfile, busy, error: failure ? localizeError(failure, locale) : "" }}>
    <ThemeProvider locale={locale} theme={theme}>{children}</ThemeProvider>
  </Context.Provider>;
}

export function usePreferences() {
  const value = useContext(Context);
  if (!value) throw new Error("PreferencesProvider is required");
  return value;
}
